// src/app/services/posthog.service.ts
import { Injectable, NgZone } from "@angular/core";
import posthog from "posthog-js";
import { environment } from "../../environments/environment";

@Injectable({ providedIn: "root" })
export class PosthogService {
  private isInitialized = false;

  constructor(private ngZone: NgZone) {}

  /**
   * Initialize PostHog without user identification
   * Called on app bootstrap
   */
  initPostHog() {
    if (this.isInitialized) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      posthog.init(environment.posthogKey, {
        api_host: environment.posthogProxy, // Use proxy to bypass ad blockers!
        ui_host: environment.posthogHost,
        person_profiles: 'identified_only', // Only create profiles for identified users
        capture_pageview: false, // We'll manually capture after user is identified
        capture_pageleave: true,
        session_recording: {
          recordCrossOriginIframes: true,
          maskAllInputs: false,
          maskTextSelector: '[data-posthog-mask]',
        },
        autocapture: {
          dom_event_allowlist: ['click', 'change', 'submit'],
          element_allowlist: ['a', 'button', 'form', 'input', 'select', 'textarea'],
        },
        loaded: (posthog) => {
          if (environment.production === false) {
            //posthog.debug();
          }
        },
      });
    });

    this.isInitialized = true;
  }

  /**
   * Identify user and start session replay
   * Called after successful authentication
   */
  identifyUser(userId: string, userEmail?: string, additionalProps?: Record<string, any>) {
    if (!this.isInitialized) {
      this.initPostHog();
    }

    this.ngZone.runOutsideAngular(() => {
      // Identify the user
      posthog.identify(userId, {
        email: userEmail,
        ...additionalProps,
      });

      // Start session recording
      posthog.startSessionRecording();

      // Capture the initial pageview after identification
      posthog.capture('$pageview');

      if (!environment.production) {
        console.log('✅ PostHog user identified:', { userId, email: userEmail });
        console.log('📹 Session recording started');
      }
    });
  }

  /**
   * Reset PostHog on sign out
   */
  reset() {
    this.ngZone.runOutsideAngular(() => {
      posthog.reset();
    });
  }

  /**
   * Capture custom events
   */
  capture(eventName: string, properties?: Record<string, any>) {
    this.ngZone.runOutsideAngular(() => {
      posthog.capture(eventName, properties);
    });
  }

  /**
   * Update user properties
   */
  setPersonProperties(properties: Record<string, any>) {
    this.ngZone.runOutsideAngular(() => {
      posthog.setPersonProperties(properties);
    });
  }
}
